import { Injectable, forwardRef, Inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, flatMap, map, tap } from 'rxjs/operators';
import * as urltemplate from 'url-template';

import { AppConstants, LMFormType } from '@utils/app.constants';
import { LMActionService, LMActionRequest } from '@services/lm-action.service';
import {
  LMFormContext,
  LMPanelTransactionArgs
} from './lm-form-action-util.service';
import { LMLoggerService } from '@services/lm-logger.service';
import {
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService
} from '@modal/lm-action-form-dialog.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMModel, LMModelMultiPartFields } from '@form/model/lm-model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { KeyedResource } from '@resources/keyed.resource';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { LMTransactionService, LMTransactionState } from '@transaction/index';
import { LMGlobalUtilService } from '@app/utils';

@Injectable()
export class LMFormTransactionService {
  constructor(
    @Inject(forwardRef(() => LMActionFormDialogService)) private lmActionFormDialogService,
    private lmActionService: LMActionService,
    private lmLoggerService: LMLoggerService,
    private lmNavigationService: LMNavigationService,
    private lmSpecService: LMSpecService,
    private lmTransactionService: LMTransactionService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
  ) {}

  private createActionRedirectUrl(
    formUrlInfo: LMFormUrlInfo,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
    const url = actionRedirectUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name,
      contextResourceId: dataView.resourceId
    });
    return url;
  }

  private getTransactionState(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): LMTransactionState {
    const formSpec = formContext.formSpec;
    const url = this.lmTransactionService.createFormTransactionUrl(formContext, actionSpec, dataView, formContext.sourceResourceId);
    const transaction = new LMTransactionState(url);
    transaction.busFormSpec = formSpec.busFormSpec;
    transaction.multiPartFields = formContext.formModel.multiPartFields;
    return transaction;
  }

  public runIndependentAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    // this is a special entry into the transaction service! We do NOT want to prompt for an action form.
    return this.lmTransactionService.runCheckResponseRequired(transaction, formContext.formSpec.busClassSpec, formContext.formSpec.busFormSpec).pipe(
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled),
      flatMap((proceed: boolean) => proceed ?
        this.lmTransactionService.executeAction(transaction) :
        of(transaction)
      )
    );
  }

  public runValidateFormPanel(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    validatePanelName: string
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({
      dataViewLists: [ dataView ],
      actionSpec,
      validateOnly: true,
      validatePanelName
    });

    transaction.setRequestPayload(transactionRequests);

    return this.lmTransactionService.executeAction(transaction);
  }

  // TODO: No longer needed?
  public runValidateKeysOnly(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const url = this.createValidateKeysOnlyTransactionUrl(formContext.formUrlInfo, formContext.formSpec, actionSpec, dataView);
    const transaction = new LMTransactionState(url);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({
      dataViewLists: [ dataView ],
      actionSpec,
      validateOnly: true,
      validatePanelName: '',
      validateKeysOnly: true
    });

    transaction.setRequestPayload(transactionRequests);

    return this.lmTransactionService.executeAction(transaction);
  }

  public runSetAction(formContext: LMFormContext, actionSpec: LMActionSpec, dataView: LMDataView): Observable<LMTransactionState> {
    return this.runInstanceAction(formContext, actionSpec, dataView);
  }

  public runInstanceAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, dataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  private runActionChecks(
    transaction: LMTransactionState,
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<boolean> {
    const formSpec = formContext.formSpec;
    const buildTransactionRequest = (scheduleView: LMDataView) =>
      this.lmActionService.buildActionRequestForDataView(
        dataView,
        actionSpec,
        null,
        null,
        null,
        scheduleView
      );

    // get schedule view details
    return this.lmTransactionService.getScheduleView(actionSpec, formSpec.busClassSpec.name).pipe(
      filter(scheduleView => (scheduleView !== undefined)),
      // build action requests payload
      map((scheduleView: LMDataView) => buildTransactionRequest(scheduleView)),
      // builds action url
      map((transactionRequest: LMActionRequest) => {
        transaction.setRequestPayload([ transactionRequest ]);
        return this.createActionRedirectUrl(formContext.formUrlInfo, actionSpec, dataView);
      }),
      // check if form input is required
      flatMap((actionUrl: string) => this.lmTransactionService.runCheckFormInputRequired(
        actionUrl,
        actionSpec,
        transaction,
        formContext.stepUpActionResult
      )),
      // proceed if form input is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, formSpec.busClassSpec, formSpec.busFormSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );
  }

  // used by panel Visited and Acknowledge action pattern
  public runPanelAction(
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    args: LMPanelTransactionArgs
  ): Observable<LMTransactionState> {
    const transaction = new LMTransactionState(args.transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    const proceed$ = this.lmTransactionService.runCheckFormInputRequired(
      args.actionRedirectUrl,
      actionSpec,
      transaction
    ).pipe(
      // proceed if form input is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, args.busClassSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runSaveAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);
    transaction.isMatrixForm = formContext.formSpec.busFormSpec && LMFormType.MATRIX === formContext.formSpec.busFormSpec.formType;

    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, dataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runDeleteAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    return this.runInstanceAction(formContext, actionSpec, dataView);
  }

  // public runCreateAction(
  //   formUrlInfo: LMFormUrlInfo,
  //   formSpec: LMFormSpec,
  //   actionSpec: LMActionSpec,
  //   dataView: LMDataView,
  //   placeholder: any
  // ): Observable<LMActionResult | LMActionFormDialogResult> {
  //   return new Observable<LMActionResult | LMActionFormDialogResult>(observer => {
  //     let createActionUrl;
  //
  //     // TODO: need drill and navigation RESOURCETYPE evaluation here
  //     if (formSpec.sourceResourceType === AppConstants.RESOURCETYPE_LIST) {
  //       let contextCreateResource = new KeyedResource({resourceId: formSpec.sourceResourceId});
  //       let dataViewCreateResource = new KeyedResource({
  //         resourceId: dataView.resourceId,
  //         keyValueArray: contextCreateResource.keyValueArray
  //       });
  //       // get decoded contextResourceId as it will be encoded in the expand()
  //       let contextResourceId = dataViewCreateResource.generateResourceId(false);
  //       const actionRedirectUrlTemplate = urltemplate.parse(formSpec.actionRedirectUrl);
  //       createActionUrl = actionRedirectUrlTemplate.expand({
  //         resourceId: contextResourceId,
  //         actionName: actionSpec.name,
  //         contextResourceId: dataView.resourceId
  //       });
  //
  //     } else if (formSpec.sourceResourceType === AppConstants.RESOURCETYPE_RELATION) {
  //       let contextCreateResource = new KeyedResource({resourceId: formSpec.fromListResourceId});
  //       let contextResourceId = contextCreateResource.resourceId;
  //       const actionRedirectUrlTemplate = urltemplate.parse(formSpec.actionRedirectUrl);
  //       createActionUrl = actionRedirectUrlTemplate.expand({
  //         // remove the list name from the resourceId
  //         resourceId: contextResourceId.substr(0, contextResourceId.indexOf('.')),
  //         actionName: actionSpec.name
  //       });
  //
  //       // remove the form parameter and add the list parameter
  //       createActionUrl = this.lmUrlUtilService.removeParam(createActionUrl, 'form');
  //       createActionUrl = this.lmUrlUtilService.appendToQueryString(createActionUrl, 'list=' + contextResourceId);
  //
  //     } else {
  //       // Using current dataview resource id in action url instead of busclassSpec name to pass upper level keys.
  //       createActionUrl = this.createActionRedirectUrl(formSpec, actionSpec, dataView);
  //     }
  //
  //     if (actionSpec.hasParameters || actionSpec.hasActionForm) {
  //       // -------------------------------------------------------------------------
  //       // Need createActionUrl to get the LMFormSpec in order for the dialog
  //       // to show the target form.
  //       // -------------------------------------------------------------------------
  //
  //       // TODO: Replace URL key template
  //       this.lmSpecService.getFormSpecFromRedirectActionUrl(createActionUrl).subscribe((response: LMActionFormUrlResponse | LMSystemException) => {
  //         if ((response as LMSystemException).status
  //           && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
  //           let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //           result.result = LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR;
  //           result.openError = {message: (response as LMSystemException).message, details: (response as LMSystemException).details, status: (response as LMSystemException).status};
  //           observer.next(result);
  //           return;
  //         }
  //         const urlCompiledTemplate = urltemplate.parse((response as LMActionFormUrlResponse).formSpec.initialDataUrl);
  //         let url = urlCompiledTemplate.expand({resourceId: (response as LMActionFormUrlResponse).resourceId, actionName: (response as LMActionFormUrlResponse).action});
  //         this.lmActionFormDialogService.getActionFormDialogDataView(url).subscribe((responseDV: any) => {
  //           let formDataView: LMDataView = responseDV;
  //           if (formDataView.status === AppConstants.STATUS_COMPLETED) {
  //             this.lmActionFormDialogService
  //             .runActionFormDialog((response as LMActionFormUrlResponse).formSpec, actionSpec, placeholder, formDataView)
  //             .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
  //               let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //               Object.assign(result, actionFormDialogResult);
  //               observer.next(result);
  //             });
  //           } else {
  //             this.lmLoggerService.error(responseDV);
  //             let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //             result.result = LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR;
  //             result.openError = {message: responseDV.message, details: responseDV.details, status: responseDV.status};
  //             observer.next(result);
  //           }
  //         });
  //       });
  //     } else {
  //       this.lmNavigationService.runCreateActionNavigation(createActionUrl);
  //     }
  //   });
  // }

  public runCreateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    stepUpActionResult?: LMActionResult
  ): Observable<LMActionFormDialogResult | void> {
    let createActionUrl: string;
    let keyedResource: KeyedResource;
    let contextResourceId: string;
    let actionRedirectUrlTemplate;

    // TODO: need drill and navigation RESOURCETYPE evaluation here
    switch (formUrlInfo.sourceResource?.resourceType) {
      case AppConstants.RESOURCETYPE_LIST:
        keyedResource = formUrlInfo.sourceResource as KeyedResource;
        const dataViewCreateResource = new KeyedResource({
          resourceId: dataView.resourceId,
          keyValueArray: keyedResource.keyValueArray
        });
        actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
        createActionUrl = actionRedirectUrlTemplate.expand({
          // get decoded contextResourceId as it will be encoded in the expand
          resourceId: dataViewCreateResource.generateResourceId(false),
          actionName: actionSpec.name,
          contextResourceId: dataView.resourceId
        });
        break;

      case AppConstants.RESOURCETYPE_RELATION:
        keyedResource = new KeyedResource({ resourceId: formUrlInfo.fromListResource.resourceId });
        contextResourceId = keyedResource.resourceId;
        actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
        createActionUrl = actionRedirectUrlTemplate.expand({
          // remove the list name from the resourceId
          resourceId: contextResourceId.substr(0, contextResourceId.lastIndexOf('.')),
          actionName: actionSpec.name
        });

        // remove the form parameter and add the list parameter
        createActionUrl = this.lmUrlUtilService.removeParam(createActionUrl, 'form');
        createActionUrl = this.lmUrlUtilService.appendToQueryString(createActionUrl, `list=${contextResourceId}`);
        break;

      default:
        // Using current dataview resource id in action url instead of busclassSpec name to pass upper level keys.
        createActionUrl = this.createActionRedirectUrl(formUrlInfo, actionSpec, dataView);
    }

    if (!actionSpec.hasParameters && !actionSpec.hasActionForm) {
      if (stepUpActionResult) {
        this.lmGlobalUtilService.navigateByUrl(stepUpActionResult.redirectUrl);
        return of();
      }
      return of(this.lmNavigationService.runCreateActionNavigation(createActionUrl));
    }

    return this.runFormActionUrl(createActionUrl, actionSpec, stepUpActionResult);
  }

  private runFormActionUrl(actionUrl: string, actionSpec: LMActionSpec, stepUpActionResult?: LMActionResult, response?: LMActionFormUrlResponse): Observable<LMActionFormDialogResult> {

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: 'TotalActionForm',
        description: 'TOTAL ACTION FORM',
        className: 'LMFormTransactionService',
        timingStart: 'LMFormTransactionService -> runFormActionUrl()',
        timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
      });
    }

    const response$ = response ? of(response) :
      this.lmSpecService.getFormSpecFromRedirectActionUrl(actionUrl, stepUpActionResult).pipe(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        map((response: LMActionFormUrlResponse | LMSystemException) => {
          // process LMSystemException
          if (response.hasOwnProperty('status')) {
            const exception = response as LMSystemException;
            const knownExceptions = [ AppConstants.STATUS_SYSTEM_ERROR, AppConstants.STATUS_UNAUTHORIZED ];
            if (knownExceptions.includes(exception.status) ) {
              throw exception;
            }
          }

          return response as LMActionFormUrlResponse;
        }),
      );

    let formUrlInfo: LMFormUrlInfo;
    let formSpec: LMFormSpec;
    const lmActionFormDialogService = (this.lmActionFormDialogService as LMActionFormDialogService);

    return response$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      flatMap((response: LMActionFormUrlResponse) => {
        formUrlInfo = response.formUrlInfo;
        formSpec = response.formSpec;

        // construct form data url
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const resourceId = response.resourceId;
        const actionName = response.action;
        const formDataUrl = urlCompiledTemplate.expand({ resourceId, actionName });
        // get form dataview
        return lmActionFormDialogService.getActionFormDialogDataView(formDataUrl);
      }),
      flatMap((formDataView: LMDataView) => {
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {
          return lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView, null, null, true);
        }
        throw formDataView as LMSystemException;
      }),
      catchError((error) => {
        // unexpected error
        if (!error.status) {
          throw error;
        }
        // handle LMSystemException
        const exception = error as LMSystemException;
        this.lmLoggerService.error(exception);

        const result = {
          result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR,
          openError: error
        } as LMActionFormDialogResult;
        return of(result);
      })
    );
  }

  // // hmmmm  test this must be for Request Action
  // public runUpdateAction(
  //     formSpec: LMFormSpec,
  //     actionSpec: LMActionSpec,
  //     formModel: LMModel,
  //     placeholder: any
  // ): Observable<LMActionResult | LMActionFormDialogResult> {
  //   return new Observable<LMActionResult | LMActionFormDialogResult>(observer => {
  //     let dataView = formModel.getData();
  //     if (actionSpec.hasParameters || actionSpec.hasActionForm) {
  //       // -------------------------------------------------------------------------
  //       // Need actionRedirectUrl to get the LMFormSpec in order for the dialog
  //       // to show the target form.
  //       // -------------------------------------------------------------------------
  //       const actionRedirectUrlTemplate = urltemplate.parse(formSpec.actionRedirectUrl);
  //       const actionRedirectUrl = actionRedirectUrlTemplate.expand({
  //         resourceId: dataView.resourceId,
  //         actionName: actionSpec.name,
  //         contextResourceId: dataView.resourceId
  //       });
  //
  //       // TODO: Replace URL key template
  //       this.lmSpecService.getFormSpecFromRedirectActionUrl(actionRedirectUrl).subscribe((response: LMActionFormUrlResponse | LMSystemException) => {
  //         if ((response as LMSystemException).status
  //           && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
  //           let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //           result.result = LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR;
  //           result.openError = {message: (response as LMSystemException).message, details: (response as LMSystemException).details, status: (response as LMSystemException).status};
  //           observer.next(result);
  //           return;
  //         }
  //         const urlCompiledTemplate = urltemplate.parse((response as LMActionFormUrlResponse).formSpec.initialDataUrl);
  //         let url = urlCompiledTemplate.expand({resourceId: (response as LMActionFormUrlResponse).resourceId, actionName: (response as LMActionFormUrlResponse).action});
  //         this.lmActionFormDialogService.getActionFormDialogDataView(url).subscribe((responseDV: any) => {
  //           let formDataView: LMDataView = responseDV;
  //           if (formDataView.status === AppConstants.STATUS_COMPLETED) {
  //             this.lmActionFormDialogService
  //                 .runActionFormDialog((response as LMActionFormUrlResponse).formSpec, actionSpec, placeholder, formDataView)
  //                 .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
  //                   let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //                   Object.assign(result, actionFormDialogResult);
  //                   observer.next(result);
  //                 });
  //           } else {
  //             this.lmLoggerService.error(responseDV);
  //             let result: LMActionFormDialogResult = new LMActionFormDialogResult();
  //             result.result = LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR;
  //             result.openError = {message: responseDV.message, details: responseDV.details, status: responseDV.status};
  //             observer.next(result);
  //           }
  //         });
  //       });
  //     } else {
  //       // Example: Employee Self Service > My Profile > Ethnicity Details
  //       // Same business class, same record but it navigates to a different form.
  //       const actionRedirectUrlTemplate = urltemplate.parse(formSpec.actionRedirectUrl);
  //       const actionRedirectUrl = actionRedirectUrlTemplate.expand({
  //         resourceId: dataView.resourceId,
  //         actionName: actionSpec.name,
  //         contextResourceId: dataView.resourceId
  //       });
  //       this.lmNavigationService.runNavigation(actionRedirectUrl);
  //     }
  //   });
  // }

  // hmmmm  test this must be for Request Action
  public runUpdateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    stepUpActionResult?: LMActionResult
  ): Observable<LMActionFormDialogResult | void> {
    // -------------------------------------------------------------------------
    // Need actionRedirectUrl to get the LMFormSpec in order for the dialog
    // to show the target form.
    // -------------------------------------------------------------------------
    const actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
    const actionRedirectUrl = actionRedirectUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name,
      contextResourceId: dataView.resourceId
    });

    if (!actionSpec.hasParameters && !actionSpec.hasActionForm) {
      if (stepUpActionResult) {
        this.lmGlobalUtilService.navigateByUrl(stepUpActionResult.redirectUrl);
        return of();
      }
      return of(this.lmNavigationService.runNavigation(actionRedirectUrl));
    }

    return this.runFormActionUrl(actionRedirectUrl, actionSpec, stepUpActionResult);
  }

  public runRelatedAction(
    relatedActionUrl: string,
    actionSpec: LMActionSpec,
    resourceId: string
  ): Observable<LMActionFormDialogResult> {
    const actionName = actionSpec.name;

    if (!actionSpec.hasParameters && !actionSpec.hasActionForm) {
      const result = {
        result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR,
        openError: { message: 'Related instance actions w/o a parameter form or action form are not yet supported' }
      } as LMActionFormDialogResult;
      return of(result);
    }

    return this.lmSpecService.getFormSpec(relatedActionUrl).pipe(
      flatMap((response: LMFormSpecResponse | LMSystemException) => {
        const formUrlInfo = (response as LMFormSpecResponse).formUrlInfo;
        const formSpec = (response as LMFormSpecResponse).formSpec;
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName });

        const actionFormUrlResponse = {
          formUrlInfo,
          formSpec,
          action: actionName,
          resourceId
        } as LMActionFormUrlResponse;
        return this.runFormActionUrl(dataUrl, actionSpec, undefined, actionFormUrlResponse);
      })
    );
  }

  public runButtonRelatedAction(
    relActionUrl: string,
    actionName: string,
    resourceId: string
  ): Observable<any> {
    const urlCompiledTemplate = urltemplate.parse(relActionUrl);
    const relatedActionUrl = urlCompiledTemplate.expand({
      resourceId,
      actionName
    });

    return this.lmSpecService.getRedirectUrl(relatedActionUrl);
  }

  public runCompareAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    // transactionURL does NOT use the action name
    const transactionUrlTemplate = urltemplate.parse(formContext.formUrlInfo.transactionUrl);
    let transactionUrl = transactionUrlTemplate.expand({ resourceId: dataView.resourceId });
    const delimiter = transactionUrl.indexOf('?') === -1 ? '?' : '&';
    transactionUrl = transactionUrl + delimiter + this.lmUrlUtilService.objectToQueryString(formContext.formUrlInfo.navigationContext);
    transactionUrl = transactionUrl + '&executionType=_executeCompare';

    const transactionState: LMTransactionState = new LMTransactionState(transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transactionState.busFormSpec = formContext.formSpec.busFormSpec;
    transactionState.multiPartFields = formContext.formModel.multiPartFields;
    transactionState.setRequestPayload(transactionRequests);

    // No response required or confirmation for compare action
    return this.lmTransactionService.executeAction(transactionState);
  }

  public runProcessFlowAction(
    formContext: LMFormContext,
    pfAction: LMProcessFlowAction,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const navigationContext = formContext.formUrlInfo.navigationContext;
    const busClassSpec = formContext.formSpec.busClassSpec;
    const busFormSpec = formContext.formSpec.busFormSpec;

    // transactionURL does NOT use the action name
    const transactionUrlTemplate = urltemplate.parse(formContext.formUrlInfo.transactionUrl);
    let transactionUrl = transactionUrlTemplate.expand({ resourceId: dataView.resourceId });
    // Add Navigation context and executionType to URL. Navigation context contains the pfi workunit parameters.
    const delimiter = transactionUrl.indexOf('?') === -1 ? '?' : '&';
    transactionUrl += delimiter + this.lmUrlUtilService.objectToQueryString(navigationContext);
    transactionUrl += '&executionType=_processflow';

    const transaction = new LMTransactionState(transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec, pfAction });
    transaction.busFormSpec = busFormSpec;
    transaction.multiPartFields = formContext.formModel.multiPartFields;
    transaction.setRequestPayload(transactionRequests);

    const proceed$ = this.lmTransactionService.runCheckResponseRequiredPF(
      transaction,
      pfAction,
      busClassSpec,
      navigationContext,
      busFormSpec
    ).pipe(
      // proceed if response PF is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, busClassSpec, busFormSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runSaveTemplateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    fromDataView: LMDataView,
    templateModels: Array<LMModel>,
    templateDataViewSet: LMDataViewSet,
    displayPanelId: string,
    displayWhenIndex?: number,
    subPanelResourceId?: string
  ): Observable<LMTransactionState> {
    this.lmLoggerService.trace([ '@FormTransactionService -> runSaveTemplateAction' ]);

    const compiledTemplate = urltemplate.parse(formUrlInfo.transactionUrl);
    const transactionUrl = compiledTemplate.expand({ resourceId: fromDataView.resourceId });
    const templateMultiPartFields = new Array<LMModelMultiPartFields>();

    for (let i = 0, len = templateModels.length; i < len; i++) {
      const model = templateModels[i];
      const dataView = model.getData();
      templateDataViewSet.data[i].dataView = dataView;
      templateMultiPartFields.push(model.multiPartFields);
    }

    if (!displayWhenIndex) {
      displayWhenIndex = 0;
    }

    const actionRequest = {
      actionSpec: AppConstants.ACTIONSPEC_TEMPLATEFORM_SAVE,
      templateDataViewSet,
      displayWhenIndex,
      subPanelResourceId,
      displayPanelId, // need for attachments
    } as LMActionRequest;

    const transaction = new LMTransactionState(transactionUrl);
    transaction.busFormSpec = formSpec.busFormSpec;
    transaction.setRequestPayload([ actionRequest ]);
    transaction.templateMultiPartFields = templateMultiPartFields;

    return this.lmTransactionService.executeAction(transaction);
  }

  public runMatrixAction(
    matrixActionTarget: LMMatrixActionTarget,
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    columnDataView: LMDataView
  ): Observable<LMTransactionState> {
    const url = this.createMatrixTransactionUrl(matrixActionTarget, formContext.formUrlInfo, actionSpec, columnDataView);
    const transaction = new LMTransactionState(url);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ columnDataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, columnDataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  private createMatrixTransactionUrl(
    matrixActionTarget: LMMatrixActionTarget,
    formUrlInfo: LMFormUrlInfo,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const transactionUrlTemplate = urltemplate.parse(matrixActionTarget === 'column' ?
      formUrlInfo.matrixColumnTransactionUrl : formUrlInfo.matrixCellTransactionUrl);

    return transactionUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    });
  }

  private createValidateKeysOnlyTransactionUrl(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const transactionUrlTemplate = urltemplate.parse(formUrlInfo.transactionUrl);
    return transactionUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    });
  }

  public processStepUpAuthActionResult(
    formContext: LMFormContext,
    actionResult: LMActionResult,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionResult.actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec: actionResult.actionSpec });
    transaction.setRequestPayload(transactionRequests);
    transaction.response = [ actionResult ];
    return this.lmTransactionService.processStepUpAuthActionResult(transaction, actionResult, transactionRequests[0]);
  }
}
